import { useState, useEffect, useRef } from "react";
import { Scale, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

function Markdown({ text }) {
  return (
    <div className="text-sm text-navy/80 leading-relaxed space-y-1" data-testid="legalai-text">
      {text.split("\n").map((ln, i) => {
        const t = ln.trim();
        if (!t) return <div key={i} className="h-2" />;
        if (t === "---") return <hr key={i} className="border-navy/10 my-2" />;
        if (t.startsWith("### ")) return <div key={i} className="font-semibold text-navy mt-2">{t.slice(4).replace(/\*\*/g, "")}</div>;
        if (t.startsWith("## ")) return <div key={i} className="font-editorial text-lg text-navy mt-3">{t.slice(3).replace(/\*\*/g, "")}</div>;
        if (t.startsWith("# ")) return <div key={i} className="font-editorial text-xl text-navy mt-2">{t.slice(2).replace(/\*\*/g, "")}</div>;
        if (/^[-*] /.test(t)) return <div key={i} className="flex gap-2 pl-1"><span className="text-violet">•</span><span>{t.slice(2).replace(/\*\*/g, "")}</span></div>;
        if (/^\d+[.)] /.test(t)) return <div key={i} className="pl-1 font-medium text-navy/90">{t.replace(/\*\*/g, "")}</div>;
        return <div key={i}>{t.replace(/\*\*/g, "")}</div>;
      })}
    </div>
  );
}

export default function ModuleLegalIntel({ moduleKey, item, onRefresh }) {
  const [busy, setBusy] = useState(item.analisis_status === "generating");
  const text = item.analisis_legal || "";
  const pollRef = useRef(null);

  useEffect(() => {
    if (item.analisis_status === "generating") {
      setBusy(true);
      pollRef.current = setInterval(() => onRefresh?.(), 4000);
    } else {
      setBusy(false);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [item.analisis_status]); // eslint-disable-line

  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/mod/${moduleKey}/analyze/${item.id}`);
      toast.success("Analizando marco legal… (puede tardar ~1 min)");
      onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo analizar"); setBusy(false); }
  };

  return (
    <div className="mt-4 border-t border-navy/10 pt-4 space-y-3" data-testid="module-legal-intel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 overline text-navy/40"><Scale className="w-3.5 h-3.5" /> Inteligencia jurídica</div>
        <div className="flex items-center gap-2">
          {text && <button onClick={() => { navigator.clipboard.writeText(text); toast.success("Copiado"); }} className="text-navy/30 hover:text-violet" title="Copiar"><Copy className="w-4 h-4" /></button>}
          <button onClick={run} disabled={busy} className="btn btn-primary" data-testid="legalai-analyze-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4" />} {text ? "Reanalizar" : "Analizar leyes aplicables"}
          </button>
        </div>
      </div>
      <p className="text-xs text-navy/45">
        La IA consultará las leyes de <b>{item.pais || "el país"}</b> en materia <b>{(item.materia || "general").toLowerCase()}</b> y explicará cómo aplican a tu ámbito <b>{(item.ambito || "general").toLowerCase()}</b>.
      </p>
      {busy && !text && <div className="py-6 flex justify-center text-navy/30"><Loader2 className="w-5 h-5 animate-spin" /> <span className="ml-2 text-sm text-navy/40">Analizando marco legal…</span></div>}
      {text && <div className="rounded-lg bg-navy/[0.03] p-3 max-h-96 overflow-y-auto"><Markdown text={text} /></div>}
    </div>
  );
}
